// Дизайн-токены TESTCS WORLD.
//
// Источник правды — эталонный лист Дениса «Complete Button and Form Component
// Reference Sheet» (docs/vision). Здесь ровно те значения: цвета, сетка 8pt,
// радиусы, размеры иконок и высоты полей. Экраны (их делает дизайнер) и
// рабочая обвязка (её делаю я) берут константы отсюда, чтобы интерфейс не
// разъехался между авторами.
//
// Импорт: import { COLORS, SPACING, RADII, ... } from '../theme'

// ─── Палитра эталона ─────────────────────────────────────────────────────────

export const COLORS = {
  // акцент и его состояния
  cyan: '#00e0ff',
  cyanHover: '#33e6ff',
  cyanPressed: '#0096b3',
  focusRing: '#33b2ff',

  // семантика
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  // бордовый — только ветка 21+, в обычном мире не используется
  adult: '#7a1e3a',

  // текст
  text: '#e6eaf0',
  textSecondary: '#94a3b8',
  textDisabled: 'rgba(230, 234, 240, 0.34)',

  // фоны панелей: почти чёрное стекло
  bgDeep: '#03070d',
  bgBase: '#071018',
  bgPanel: 'rgba(6, 14, 22, 0.94)',
  bgPanelLight: 'rgba(12, 22, 33, 0.82)',
  bgElevated: 'rgba(18, 30, 42, 0.92)',
  disabled: '#334155',

  // границы и подсветка
  border: 'rgba(148, 163, 184, 0.16)',
  borderStrong: 'rgba(0, 224, 255, 0.42)',
  cyanSoft: 'rgba(0, 224, 255, 0.12)',
  cyanGlow: 'rgba(0, 224, 255, 0.38)',
  successSoft: 'rgba(34, 197, 94, 0.14)',
  warningSoft: 'rgba(245, 158, 11, 0.14)',
  errorSoft: 'rgba(239, 68, 68, 0.14)',
  adultSoft: 'rgba(122, 30, 58, 0.18)',
}

// ─── Сетка 8pt ───────────────────────────────────────────────────────────────
// Ряд из эталона: 4 8 12 16 24 32 40 48 56 64 72 80 88 96 104 112 120

export const SPACING = [4, 8, 12, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96, 104, 112, 120]
export const space = step => `${SPACING[step] ?? SPACING[SPACING.length - 1]}px`

// ─── Радиусы, иконки, высоты полей ───────────────────────────────────────────

export const RADII = { xs: '2px', sm: '4px', md: '6px', lg: '8px', xl: '12px', xxl: '16px', pill: '24px', round: '999px' }
export const ICON_SIZES = { xs: 16, sm: 20, md: 24, lg: 32, xl: 40, xxl: 48, hero: 64 }
export const INPUT_HEIGHTS = { small: 32, medium: 40, large: 48 }
// палец на телефоне: минимальная цель по гайдлайнам мобильных ОС
export const TOUCH_MIN = 44

// ─── Тени и стекло ───────────────────────────────────────────────────────────

export const SHADOWS = {
  panel: '0 1.4rem 3.2rem rgba(0, 0, 0, 0.55)',
  raised: '0 0.6rem 1.6rem rgba(0, 0, 0, 0.42)',
  focus: `0 0 0 2px ${COLORS.focusRing}`,
  glowCyan: `0 0 1.4rem ${COLORS.cyanGlow}`,
}

export const GLASS = {
  light: 'blur(10px) saturate(120%)',
  base: 'blur(18px) saturate(130%)',
  heavy: 'blur(28px) saturate(140%)',
}

// ─── Типографика ─────────────────────────────────────────────────────────────

export const TYPE = {
  hero: { size: '2.4rem', weight: 750, spacing: '-0.02em' },
  title: { size: '1.15rem', weight: 700, spacing: '0.18em' },
  section: { size: '0.78rem', weight: 650, spacing: '0.22em' },
  body: { size: '0.9rem', weight: 500, spacing: '0.01em' },
  // 16px — иначе мобильные браузеры зумят поле при фокусе
  input: { size: '1rem', weight: 500, spacing: '0' },
  caption: { size: '0.72rem', weight: 500, spacing: '0.06em' },
}

// ─── Движение ────────────────────────────────────────────────────────────────

export const MOTION = {
  hover: '0.12s ease-out',
  enter: '0.2s ease-out',
  panel: '0.28s cubic-bezier(0.22, 1, 0.36, 1)',
  pulse: '1.8s ease-in-out infinite',
}

export const ACCESSIBILITY_TOKENS = {
  contrastFilter: 'contrast(1.22)',
  highFocus: '#ffffff',
  highFocusBacking: '#000000',
  reducedDuration: '0.01ms',
}

// Состояния кнопок из эталона — чтобы все авторы называли их одинаково.
export const BUTTON_VARIANTS = ['primary', 'secondary', 'ghost', 'icon', 'destructive', 'success', 'warning', 'adult']
export const CONTROL_STATES = ['normal', 'hover', 'pressed', 'focused', 'disabled', 'loading']

// CSS-переменные: вешаются на корень интерфейса один раз, дальше любой
// компонент берёт var(--testcs-*) без импорта.
export const CSS_VARS = `
  --testcs-cyan: ${COLORS.cyan};
  --testcs-cyan-hover: ${COLORS.cyanHover};
  --testcs-cyan-pressed: ${COLORS.cyanPressed};
  --testcs-focus: ${COLORS.focusRing};
  --testcs-success: ${COLORS.success};
  --testcs-warning: ${COLORS.warning};
  --testcs-error: ${COLORS.error};
  --testcs-adult: ${COLORS.adult};
  --testcs-text: ${COLORS.text};
  --testcs-text-secondary: ${COLORS.textSecondary};
  --testcs-bg-deep: ${COLORS.bgDeep};
  --testcs-bg-base: ${COLORS.bgBase};
  --testcs-panel: ${COLORS.bgPanel};
  --testcs-panel-light: ${COLORS.bgPanelLight};
  --testcs-border: ${COLORS.border};
  --testcs-border-strong: ${COLORS.borderStrong};
  --testcs-radius: ${RADII.xl};
  --testcs-glass: ${GLASS.base};
  --testcs-reduced-duration: ${ACCESSIBILITY_TOKENS.reducedDuration};
`
